import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  UserPlusIcon,
  UserCircleIcon,
  PencilSquareIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const handleLogout = () => {
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('company');
  window.location.href = '/';
};

/* Menü yapısı: adminOnly olanlar sadece role === '1' için görünür */
const NAV_SECTIONS = [
  {
    title: 'Genel',
    items: [
      { to: '/home', label: 'Anasayfa', Icon: HomeIcon },
    ],
  },
  {
    title: 'Yönetim',
    adminOnly: true,
    items: [
      {
        key: 'personel',
        label: 'Personel',
        Icon: UsersIcon,
        children: [
          { to: '/pjoin', label: 'Personel Kayıt', Icon: UserPlusIcon },
          { to: '/pedit', label: 'Personel Takip', Icon: UserCircleIcon },
          { to: '/person', label: 'Personel Düzenle', Icon: PencilSquareIcon },
        ],
      },
      {
        key: 'izin',
        label: 'İzin Talep / Onay',
        Icon: ClipboardDocumentListIcon,
        children: [
          { to: '/permission', label: 'İzin Kayıt', Icon: ClipboardDocumentListIcon },
          { to: '/editpermission', label: 'İzin Onay', Icon: ClipboardDocumentCheckIcon },
        ],
      },
      { to: '/company', label: 'Kurum Bilgileri', Icon: BuildingOffice2Icon },
      { to: '/hakedis', label: 'Hakediş', Icon: BanknotesIcon },
    ],
  },
  {
    title: 'Raporlar',
    items: [
      { to: '/reports', label: 'Raporlar', Icon: ChartBarIcon },
    ],
  },
  {
    title: 'Takvim',
    items: [
      { to: '/help', label: 'Tatil Takvimi', Icon: CalendarDaysIcon },
    ],
  },
];

const Sidebar = ({ collapsed = false }) => {
  const location = useLocation();
  const role = localStorage.getItem('role');
  const isAdmin = role === '1';

  const childPaths = (item) => (item.children || []).map((c) => c.to);

  // Aktif rotayı içeren grup açık başlasın
  const [openGroups, setOpenGroups] = useState(() => {
    const open = {};
    NAV_SECTIONS.forEach((s) =>
      s.items.forEach((i) => {
        if (i.children && childPaths(i).includes(location.pathname)) open[i.key] = true;
      })
    );
    return open;
  });

  useEffect(() => {
    NAV_SECTIONS.forEach((s) =>
      s.items.forEach((i) => {
        if (i.children && childPaths(i).includes(location.pathname)) {
          setOpenGroups((g) => (g[i.key] ? g : { ...g, [i.key]: true }));
        }
      })
    );
  }, [location.pathname]);

  const sections = NAV_SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  return (
    <div className="sidebar">
      <nav className="sidebar-nav">
        {sections.map((section) => (
          <div className="sidebar-nav__section" key={section.title}>
            <p className="sidebar-nav__section-title">{section.title}</p>
            <ul className="sidebar-nav__list">
              {section.items.map((item) => {
                if (item.children) {
                  const groupActive = childPaths(item).includes(location.pathname);
                  const isOpen = !collapsed && !!openGroups[item.key];
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={`sidebar-nav__item ${groupActive ? 'sidebar-nav__item--active' : ''}`}
                        onClick={() => setOpenGroups((g) => ({ ...g, [item.key]: !g[item.key] }))}
                        aria-expanded={isOpen}
                        title={item.label}
                      >
                        <item.Icon className="sidebar-nav__icon" aria-hidden="true" />
                        <span className="sidebar-nav__label">{item.label}</span>
                        <ChevronDownIcon
                          className={`sidebar-nav__caret ${isOpen ? 'is-open' : ''}`}
                          aria-hidden="true"
                        />
                      </button>

                      {isOpen && (
                        <ul className="sidebar-nav__sublist">
                          {item.children.map((child) => (
                            <li key={child.to}>
                              <Link
                                to={child.to}
                                className={`sidebar-nav__item ${
                                  location.pathname === child.to ? 'sidebar-nav__item--active' : ''
                                }`}
                                title={child.label}
                              >
                                <child.Icon className="sidebar-nav__icon" aria-hidden="true" />
                                <span className="sidebar-nav__label">{child.label}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`sidebar-nav__item ${
                        location.pathname === item.to ? 'sidebar-nav__item--active' : ''
                      }`}
                      title={item.label}
                    >
                      <item.Icon className="sidebar-nav__icon" aria-hidden="true" />
                      <span className="sidebar-nav__label">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="sidebar__footer">
        <button type="button" onClick={handleLogout} className="sidebar-nav__item" title="Çıkış Yap">
          <ArrowRightOnRectangleIcon className="sidebar-nav__icon" aria-hidden="true" />
          <span className="sidebar-nav__label">Çıkış Yap</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
